import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertCircle, Scale, Shield, Mail } from 'lucide-react';
import ContactModal from '../components/ContactModal';
import StandardFooter from '../components/StandardFooter';

const TermsPage = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <div className="legal-header-content">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <img src="/logo.png" alt="Offworld Ventures" className="legal-logo" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="legal-hero">
        <h1 className="legal-title">Terms of Service</h1>
        <p className="legal-subtitle">Clear rules. Fair terms. Mutual respect.</p>
        <div className="legal-meta">
          <span>Effective Date: December 31, 2024</span>
          <span className="legal-divider">•</span>
          <span>Last Updated: December 31, 2024</span>
        </div>
      </section>

      {/* Content */}
      <section className="legal-content">
        <div className="legal-container">
          
          {/* Introduction */}
          <div className="legal-section">
            <div className="legal-section-header">
              <AlertCircle size={24} />
              <h2>Agreement to Terms</h2>
            </div>
            <p>
              Welcome to Offworld Ventures, LLC ("Offworld," "we," "us," or "our"). By accessing or using our website and services, you agree to be bound by these Terms of Service ("Terms"). If you don't agree, please don't use our services.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Offworld Ventures, LLC. Please read them carefully.
            </p>
          </div>

          {/* Eligibility */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Shield size={24} />
              <h2>Eligibility</h2>
            </div>
            <p>
              You must be at least 18 years old and have the legal capacity to enter into binding contracts to use our services. By using our services, you represent and warrant that you meet these requirements.
            </p>
          </div>

          {/* Services Description */}
          <div className="legal-section">
            <div className="legal-section-header">
              <FileText size={24} />
              <h2>Our Services</h2>
            </div>
            <p>
              Offworld Ventures is an AI-first venture studio that provides:
            </p>
            <ul>
              <li>Custom software development — web apps, mobile apps, SaaS platforms, AI tools, and internal tools</li>
              <li>AI-powered product design, prototyping, and engineering</li>
              <li>Opportunities to engage with our team regarding project collaborations</li>
              <li>Access to our in-house portfolio of products and technology</li>
            </ul>
            <p>
              <strong>Important:</strong> All project timelines, deliverables, and scope are subject to separate engagement agreements. Information on this website is for general informational purposes only.
            </p>
          </div>

          {/* User Responsibilities */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Scale size={24} />
              <h2>User Responsibilities</h2>
            </div>
            <p>When using our services, you agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not use our services for any unlawful or prohibited purpose</li>
              <li>Not interfere with or disrupt the integrity or performance of our services</li>
              <li>Not attempt to gain unauthorized access to our systems or networks</li>
              <li>Respect intellectual property rights of Offworld and third parties</li>
            </ul>
          </div>

          {/* Intellectual Property */}
          <div className="legal-section">
            <div className="legal-section-header">
              <FileText size={24} />
              <h2>Intellectual Property Rights</h2>
            </div>
            <div className="legal-subsection">
              <h3>Our Content</h3>
              <p>
                All content on this website—including text, graphics, logos, images, and software—is the property of Offworld Ventures, LLC or its licensors and is protected by copyright, trademark, and other intellectual property laws.
              </p>
            </div>
            <div className="legal-subsection">
              <h3>Limited License</h3>
              <p>
                We grant you a limited, non-exclusive, non-transferable license to access and use our website for personal, non-commercial purposes. You may not reproduce, distribute, modify, or create derivative works without our explicit written permission.
              </p>
            </div>
            <div className="legal-subsection">
              <h3>Your Content</h3>
              <p>
                When you submit information to us (e.g., project briefs, requirements documents), you retain ownership of your content. However, you grant us a license to use, reproduce, and share that content for the purpose of evaluating and delivering project work.
              </p>
            </div>
          </div>

          {/* Confidentiality */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Shield size={24} />
              <h2>Confidentiality</h2>
            </div>
            <p>
              We understand that clients share sensitive information. While we treat all submissions confidentially, we recommend executing a separate Non-Disclosure Agreement (NDA) for highly sensitive or proprietary information.
            </p>
            <p>
              We will not share your proprietary information with third parties without your consent, except as required by law or to deliver on project engagements with our team and partners.
            </p>
          </div>

          {/* Project Disclaimer */}
          <div className="legal-section legal-disclaimer">
            <div className="legal-section-header">
              <AlertCircle size={24} />
              <h2>Project & Services Disclaimer</h2>
            </div>
            <p>
              <strong>No Guaranteed Outcomes:</strong> While we are committed to delivering high-quality work, we do not guarantee specific business outcomes, revenue targets, or performance metrics for any product or service we build.
            </p>
            <p>
              <strong>Engagement Terms:</strong> Submission of a project inquiry does not guarantee engagement, partnership, or any business relationship. All project engagements are subject to separate written agreements.
            </p>
            <p>
              <strong>Third-Party Dependencies:</strong> Products built by Offworld may rely on third-party services, APIs, and infrastructure. We are not liable for outages, changes, or discontinuation of third-party services.
            </p>
            <p>
              <strong>Intellectual Property:</strong> Ownership of deliverables is governed by individual project agreements. Unless otherwise specified, client-commissioned work is owned by the client upon full payment.
            </p>
          </div>

          {/* Disclaimers */}
          <div className="legal-section">
            <div className="legal-section-header">
              <AlertCircle size={24} />
              <h2>Disclaimers & Limitations</h2>
            </div>
            <div className="legal-subsection">
              <h3>"As Is" Basis</h3>
              <p>
                Our services are provided "as is" and "as available" without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
              </p>
            </div>
            <div className="legal-subsection">
              <h3>No Guarantee of Accuracy</h3>
              <p>
                While we strive for accuracy, we do not guarantee that our website or content is error-free, complete, or current. Information may be changed or updated without notice.
              </p>
            </div>
            <div className="legal-subsection">
              <h3>Third-Party Links</h3>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of service of these external sites.
              </p>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Scale size={24} />
              <h2>Limitation of Liability</h2>
            </div>
            <p>
              To the maximum extent permitted by law, Offworld Ventures, LLC, its affiliates, officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
            </p>
            <ul>
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss of data or information</li>
              <li>Business interruption</li>
              <li>Personal injury or property damage</li>
            </ul>
            <p>
              Our total liability shall not exceed the amount you paid to us (if any) in the twelve months preceding the claim.
            </p>
          </div>

          {/* Indemnification */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Shield size={24} />
              <h2>Indemnification</h2>
            </div>
            <p>
              You agree to indemnify, defend, and hold harmless Offworld Ventures, LLC and its affiliates from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or related to:
            </p>
            <ul>
              <li>Your use or misuse of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Any content you submit or transmit through our services</li>
            </ul>
          </div>

          {/* Termination */}
          <div className="legal-section">
            <div className="legal-section-header">
              <AlertCircle size={24} />
              <h2>Termination</h2>
            </div>
            <p>
              We reserve the right to suspend or terminate your access to our services at any time, with or without notice, for any reason, including but not limited to violation of these Terms.
            </p>
            <p>
              Upon termination, your right to use our services will immediately cease. Provisions that by their nature should survive termination will remain in effect.
            </p>
          </div>

          {/* Governing Law */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Scale size={24} />
              <h2>Governing Law & Dispute Resolution</h2>
            </div>
            <div className="legal-subsection">
              <h3>Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of New Mexico, United States, without regard to its conflict of law provisions.
              </p>
            </div>
            <div className="legal-subsection">
              <h3>Dispute Resolution</h3>
              <p>
                Any dispute arising out of or relating to these Terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. The arbitration shall be conducted in New Mexico, and judgment on the award may be entered in any court having jurisdiction.
              </p>
            </div>
            <div className="legal-subsection">
              <h3>Class Action Waiver</h3>
              <p>
                You agree to resolve disputes with us on an individual basis and waive your right to participate in class actions or class arbitrations.
              </p>
            </div>
          </div>

          {/* Changes to Terms */}
          <div className="legal-section">
            <div className="legal-section-header">
              <FileText size={24} />
              <h2>Changes to These Terms</h2>
            </div>
            <p>
              We may modify these Terms at any time. We'll notify you of material changes by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the modified Terms.
            </p>
            <p>
              We encourage you to review these Terms periodically to stay informed of updates.
            </p>
          </div>

          {/* Severability */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Scale size={24} />
              <h2>Severability & Waiver</h2>
            </div>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
            </p>
            <p>
              Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.
            </p>
          </div>

          {/* Contact */}
          <div className="legal-section legal-contact">
            <div className="legal-section-header">
              <h2>Contact Us</h2>
            </div>
            <p>
              Questions about these Terms? We're here to clarify.
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <strong>Company:</strong> Offworld Ventures, LLC
              </div>
              <div className="contact-item">
                <strong>Location:</strong> Based on Earth - For Now
              </div>
            </div>
            <button className="contact-cta-button" onClick={() => setIsContactOpen(true)}>
              <Mail size={20} />
              Get in Touch
            </button>
          </div>

        </div>
      </section>

      {/* Footer */}
      <StandardFooter onContactClick={() => setIsContactOpen(true)} />

      {/* Contact Modal */}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </div>
  );
};

export default TermsPage;